import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import SummaryApi from "./common/index";

// Import local mobile banner images
import sathyaImg from "../assets/sathya.webp";
import mahaImg from "../assets/HeroBanners/MobileBanners/maha.webp";
import vajramImg from "../assets/HeroBanners/MobileBanners/vajram.webp";
import subhamImg from "../assets/HeroBanners/MobileBanners/subham.webp";

// Desktop route map corresponding to Image1, Image2, Image3, Image4 order
const DESKTOP_PROJECT_ROUTES = [
  "/project/satyas-elite",
  "/project/mahalakshmi-golden-city",
  "/project/vajram",
  "/project/subham-avenues",
];

const MOBILE_PROJECTS = [
  {
    title: "SATYA’S ELITE",
    description:
      "A fully developed land area with a focus on residential plots and community spaces. @Kurumaddali",
    image: sathyaImg,
    link: "/project/satyas-elite",
  },
  {
    title: "MAHALAKSHMI'S GOLDEN CITY",
    description:
      "A prime location expansion with plots suitable for mixed-use development. @Chilakaluripeta",
    image: mahaImg,
    link: "/project/mahalakshmi-golden-city",
  },
  {
    title: "VAJRAM",
    description:
      "A prime location expansion with plots suitable for mixed-use development. @Tadikonda",
    image: vajramImg,
    link: "/project/vajram",
  },
  {
    title: "SUBHAM AVENUES",
    description:
      "A fully developed land area with a focus on residential plots and community spaces. @Pamarru",
    image: subhamImg,
    link: "/project/subham-avenues",
  },
];

// --- Validation helpers ---
const isValidMobile = (value) => /^[0-9]{10}$/.test(value);
const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// --- Submit hook for mobile & tablet forms ---
function useEnquiryForm({ projects, currentSlide, onSubmitEnquiry }) {
  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const trimmedName = formData.fullName.trim();
  const trimmedMobile = formData.mobile.trim();
  const trimmedEmail = formData.email.trim();

  const isFormValid =
    trimmedName.length > 0 &&
    isValidMobile(trimmedMobile) &&
    (trimmedEmail.length === 0 || isValidEmail(trimmedEmail));

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const digitsOnly = value.replace(/[^0-9]/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: digitsOnly }));
      if (errorMsg) setErrorMsg("");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMsg("");

    const projectTitle = projects[currentSlide]?.title;

    const payload = new FormData();
    payload.append("name", trimmedName);
    payload.append("mobileNumber", trimmedMobile);
    payload.append("email", trimmedEmail);
    payload.append("project", projectTitle || "");

    try {
      const response = await fetch(SummaryApi.popupForm.url, {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        throw new Error("Failed to submit the form. Please try again.");
      }

      if (onSubmitEnquiry) {
        onSubmitEnquiry({
          fullName: trimmedName,
          mobile: trimmedMobile,
          email: trimmedEmail,
          project: projectTitle,
        });
      }

      setIsSubmitted(true);
      setFormData({ fullName: "", mobile: "", email: "" });
      setTimeout(() => setIsSubmitted(false), 4000);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitted,
    isSubmitting,
    errorMsg,
    isFormValid,
    handleInputChange,
    handleSubmit,
  };
}

// --- 1. Mobile-Only View ---
function MobileSliderWithForm({
  projects = MOBILE_PROJECTS,
  interval = 5000,
  onSubmitEnquiry,
}) {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  const totalSlides = projects.length;

  const nextSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (totalSlides > 1) {
      intervalRef.current = setInterval(nextSlide, interval);
    }
  }, [nextSlide, interval, totalSlides]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetTimer]);

  const {
    formData,
    isSubmitted,
    isSubmitting,
    errorMsg,
    isFormValid,
    handleInputChange,
    handleSubmit,
  } = useEnquiryForm({ projects, currentSlide, onSubmitEnquiry });

  if (!projects || projects.length === 0) return null;

  const currentProject = projects[currentSlide];

  return (
    <div className="w-full bg-[#FAF6ED] min-h-screen flex flex-col font-sans m-0 p-0 leading-none select-none pb-8">
      {/* Clickable Mobile Slider Image */}
      <div className="relative w-full h-auto overflow-hidden m-0 p-0 leading-none">
        {projects.map((slide, index) => {
          const isActive = index === currentSlide;
          return (
            <div
              key={index}
              onClick={() => slide.link && navigate(slide.link)}
              className={`w-full transition-opacity duration-700 ease-in-out m-0 p-0 cursor-pointer ${isActive
                ? "relative opacity-100 z-10 pointer-events-auto"
                : "absolute inset-0 opacity-0 z-0 pointer-events-none"
                }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-auto block m-0 p-0 border-0 align-top"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          );
        })}

        {totalSlides > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prevSlide();
                resetTimer();
              }}
              aria-label="Previous slide"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center border border-white/70 shadow-md active:scale-95 transition-all text-xs"
            >
              &#10094;
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                nextSlide();
                resetTimer();
              }}
              aria-label="Next slide"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center border border-white/70 shadow-md active:scale-95 transition-all text-xs"
            >
              &#10095;
            </button>
          </>
        )}
      </div>

      {/* Slide Content (Clickable) */}
      <div
        onClick={() => currentProject.link && navigate(currentProject.link)}
        className="px-6 pt-5 pb-3 text-center leading-normal cursor-pointer"
      >
        <h2 className="text-xl font-bold tracking-wide font-serif text-neutral-900 uppercase hover:text-emerald-700 transition">
          {currentProject.title}
        </h2>
        <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed max-w-xs mx-auto">
          {currentProject.description}
        </p>
      </div>

      {/* Enquire Form */}
      <div className="px-5 mt-2 leading-normal">
        <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-neutral-100">
          <h3 className="text-xl font-serif text-center font-semibold text-neutral-800 mb-5">
            Enquire Now
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your Full Name"
                required
                className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
                Mobile
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Enter your Mobile Number"
                maxLength={10}
                inputMode="numeric"
                required
                className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-white border border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:border-[#00875a] transition-all"
              />
              {formData.mobile.length > 0 && !isValidMobile(formData.mobile) && (
                <p className="text-[11px] text-red-500 mt-1">
                  Enter exactly 10 numeric digits.
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
                Email <span className="text-neutral-500 font-normal">(optional)</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-white border border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:border-[#00875a] transition-all"
              />
              {formData.email.length > 0 && !isValidEmail(formData.email) && (
                <p className="text-[11px] text-red-500 mt-1">
                  Enter a valid email address.
                </p>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full py-3 bg-[#008a5e] text-white font-bold text-sm tracking-wider uppercase rounded-xl shadow-md transition-all ${!isFormValid || isSubmitting
                  ? "cursor-not-allowed"
                  : "hover:bg-[#00744f] active:scale-[0.99] cursor-pointer"
                  }`}
              >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
              </button>
            </div>

            {errorMsg && (
              <p className="text-xs text-center text-red-500 font-medium pt-1">
                {errorMsg}
              </p>
            )}

            {isSubmitted && (
              <p className="text-xs text-center text-emerald-600 font-medium pt-1">
                Thank you! Your enquiry has been received.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// --- 2. Tablet-Only View ---
function TabletSliderWithForm({
  projects = MOBILE_PROJECTS,
  interval = 5000,
  onSubmitEnquiry,
}) {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  const totalSlides = projects.length;

  const nextSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (totalSlides > 1) {
      intervalRef.current = setInterval(nextSlide, interval);
    }
  }, [nextSlide, interval, totalSlides]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetTimer]);

  const {
    formData,
    isSubmitted,
    isSubmitting,
    errorMsg,
    isFormValid,
    handleInputChange,
    handleSubmit,
  } = useEnquiryForm({ projects, currentSlide, onSubmitEnquiry });

  if (!projects || projects.length === 0) return null;

  const currentProject = projects[currentSlide];

  return (
    <div className="w-full bg-[#FAF6ED] min-h-auto flex flex-col font-sans m-0 p-0 leading-none select-none pb-8">
      {/* Clickable Tablet Slider Image */}
      <div className="relative w-full h-auto overflow-hidden m-0 p-0 leading-none">
        {projects.map((slide, index) => {
          const isActive = index === currentSlide;
          return (
            <div
              key={index}
              onClick={() => slide.link && navigate(slide.link)}
              className={`w-full transition-opacity duration-700 ease-in-out m-0 p-0 cursor-pointer ${isActive
                ? "relative opacity-100 z-10 pointer-events-auto"
                : "absolute inset-0 opacity-0 z-0 pointer-events-none"
                }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-auto block m-0 p-0 border-0 align-top"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          );
        })}

        {totalSlides > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prevSlide();
                resetTimer();
              }}
              aria-label="Previous slide"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center border border-white/70 shadow-md active:scale-95 transition-all text-sm"
            >
              &#10094;
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                nextSlide();
                resetTimer();
              }}
              aria-label="Next slide"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center border border-white/70 shadow-md active:scale-95 transition-all text-sm"
            >
              &#10095;
            </button>
          </>
        )}
      </div>

      {/* Slide Content (Clickable) */}
      <div
        onClick={() => currentProject.link && navigate(currentProject.link)}
        className="px-6 pt-8 pb-3 text-center leading-normal cursor-pointer"
      >
        <h2 className="text-2xl font-bold tracking-wide font-serif text-neutral-900 uppercase hover:text-emerald-700 transition">
          {currentProject.title}
        </h2>
        <p className="text-sm text-neutral-600 mt-2 leading-relaxed max-w-lg mx-auto">
          {currentProject.description}
        </p>
      </div>

      {/* Enquire Form */}
      <div className="px-5 mt-10 leading-normal mb-10">
        <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-neutral-100 max-w-xl mx-auto">
          <h3 className="text-xl font-serif text-center font-semibold text-neutral-800 mb-5">
            Enquire Now
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your Full Name"
                required
                className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:bg-white transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
                Mobile
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Enter your Mobile Number"
                maxLength={10}
                inputMode="numeric"
                required
                className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-white border border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:border-[#00875a] transition-all"
              />
              {formData.mobile.length > 0 && !isValidMobile(formData.mobile) && (
                <p className="text-[11px] text-red-500 mt-1">
                  Enter exactly 10 numeric digits.
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
                Email <span className="text-neutral-500 font-normal">(optional)</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-white border border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:border-[#00875a] transition-all"
              />
              {formData.email.length > 0 && !isValidEmail(formData.email) && (
                <p className="text-[11px] text-red-500 mt-1">
                  Enter a valid email address.
                </p>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full py-3 bg-[#008a5e] text-white font-bold text-sm tracking-wider uppercase rounded-xl shadow-md transition-all ${!isFormValid || isSubmitting
                  ? "cursor-not-allowed"
                  : "hover:bg-[#00744f] active:scale-[0.99] cursor-pointer"
                  }`}
              >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
              </button>
            </div>

            {errorMsg && (
              <p className="text-xs text-center text-red-500 font-medium pt-1">
                {errorMsg}
              </p>
            )}

            {isSubmitted && (
              <p className="text-xs text-center text-emerald-600 font-medium pt-1">
                Thank you! Your enquiry has been received.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// --- 3. Desktop Slider Component ---
function DesktopSlider({ images = [], effects = [], interval = 5000 }) {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentEffectIndex, setCurrentEffectIndex] = useState(0);
  const intervalRef = useRef(null);

  const totalSlides = images.length;
  const totalEffects = effects.length;

  const nextSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    if (totalEffects > 0) {
      setCurrentEffectIndex((prev) => (prev + 1) % totalEffects);
    }
  }, [totalSlides, totalEffects]);

  const prevSlide = useCallback(() => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    if (totalEffects > 0) {
      setCurrentEffectIndex((prev) => (prev - 1 + totalEffects) % totalEffects);
    }
  }, [totalSlides, totalEffects]);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (totalSlides > 1) {
      intervalRef.current = setInterval(nextSlide, interval);
    }
  }, [nextSlide, interval, totalSlides]);

  useEffect(() => {
    if (totalSlides > 0 && currentSlide >= totalSlides) {
      setCurrentSlide(0);
    }
  }, [totalSlides, currentSlide]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetTimer]);

  const handleManualNext = () => {
    nextSlide();
    resetTimer();
  };

  const handleManualPrev = () => {
    prevSlide();
    resetTimer();
  };

  const handleDotClick = (idx) => {
    setCurrentSlide(idx);
    if (totalEffects > 0) {
      setCurrentEffectIndex(idx % totalEffects);
    }
    resetTimer();
  };

  if (!images || images.length === 0) return null;

  const currentEffectClass = effects[currentEffectIndex] || "";

  const handleBannerClick = (index) => {
    const route = DESKTOP_PROJECT_ROUTES[index % DESKTOP_PROJECT_ROUTES.length];
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="relative w-full h-auto overflow-hidden select-none m-0 p-0 leading-none">
      <div className={`slider ${currentEffectClass} relative w-full h-auto m-0 p-0`}>
        {images.map((image, index) => {
          const isActive = index === currentSlide;
          const imgSrc = typeof image === "string" ? image : image?.image;

          return (
            <div
              key={index}
              onClick={() => handleBannerClick(index)}
              className={`w-full transition-opacity duration-700 ease-in-out cursor-pointer ${isActive
                ? "relative opacity-100 z-10 pointer-events-auto"
                : "absolute inset-0 opacity-0 z-0 pointer-events-none"
                }`}
            >
              <img
                src={imgSrc}
                alt={`Slide ${index + 1}`}
                className="w-full h-auto block m-0 p-0 border-0"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          );
        })}
      </div>

      {totalSlides > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleManualPrev();
            }}
            aria-label="Previous slide"
            className="prev button absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-300 text-xl shadow-lg border border-white/20 cursor-pointer"
          >
            ❮
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleManualNext();
            }}
            aria-label="Next slide"
            className="next button absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-300 text-xl shadow-lg border border-white/20 cursor-pointer"
          >
            ❯
          </button>
        </>
      )}

      {totalSlides > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center space-x-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDotClick(idx);
              }}
              aria-label={`Go to slide ${idx + 1}`}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${idx === currentSlide
                ? "w-6 bg-white shadow-md"
                : "w-2 bg-white/50 hover:bg-white/80"
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// --- 4. Main Exported Slider Entry Point ---
function Slider({
  images = [],
  mobileImages = [],
  tabImages = [],
  effects = [],
  interval = 5000,
  onSubmitEnquiry,
}) {
  const isMobile = useMediaQuery({ maxWidth: 462 });
  const isTablet = useMediaQuery({ minWidth: 463, maxWidth: 1024 });

  if (isMobile) {
    return (
      <MobileSliderWithForm
        projects={MOBILE_PROJECTS}
        interval={interval}
        onSubmitEnquiry={onSubmitEnquiry}
      />
    );
  }

  if (isTablet) {
    return (
      <TabletSliderWithForm
        projects={MOBILE_PROJECTS}
        interval={interval}
        onSubmitEnquiry={onSubmitEnquiry}
      />
    );
  }

  return (
    <DesktopSlider
      images={images}
      effects={effects}
      interval={interval}
    />
  );
}

export default Slider;